/**
 * Decide whether a machine is in the "running" state from a live status
 * transition.
 *
 * The status name is localized by the edge system (e.g. English "Running",
 * German "MASCHINE LÄUFT"), so matching the English word alone is wrong — a
 * German shift would show as not-running (orange) even while producing. We
 * therefore match running terms across languages, and (preferably) the numeric
 * status code which is language-independent.
 *
 * If you know the exact running status code(s) from the edge system, add them
 * to RUNNING_STATUS_CODES and detection becomes fully language-proof.
 */
export const RUNNING_STATUS_CODES = new Set<number>([
  // e.g. 2, — fill in once the running code is confirmed
]);

const RUNNING_NAME_PATTERNS = [
  "running", // EN
  "läuft", // DE (umlaut)
  "laeuft", // DE (ae transliteration)
  "lauft", // DE (no umlaut)
];

export function isMachineRunning(
  statusName?: string | null,
  statusCode?: number | null,
): boolean {
  if (typeof statusCode === "number" && RUNNING_STATUS_CODES.has(statusCode)) {
    return true;
  }
  if (!statusName) return false;
  const n = statusName.toLowerCase();
  return RUNNING_NAME_PATTERNS.some((p) => n.includes(p));
}

// Status names that are NOT the machine's own fault/stop:
//  - "(ext)" → line-induced wait (starved/blocked by a neighbour) = knock-on
//  - standby / setup (changeover) → planned, not a fault
const EXTERNAL_WAIT_PATTERNS = ["(ext)", "(extern"];
const NON_FAULT_PATTERNS = [
  "standby",
  "einricht", // EINRICHTBETRIEB (setup)
  "setup",
  "rüst", // Rüsten (changeover)
  "ruest",
];

/**
 * Whether a status represents the machine's OWN fault/stop time — i.e. an
 * unplanned stop caused by this machine (internal fault or generic stopped),
 * as opposed to running, planned standby/setup, or a line-induced ("(EXT)")
 * wait. Used to total real downtime, excluding idle/waiting/knock-on.
 */
export function isFaultStopStatus(
  statusName?: string | null,
  statusCode?: number | null,
): boolean {
  if (isMachineRunning(statusName, statusCode)) return false;
  if (!statusName) return false;
  const n = statusName.toLowerCase();
  if (EXTERNAL_WAIT_PATTERNS.some((p) => n.includes(p))) return false;
  if (NON_FAULT_PATTERNS.some((p) => n.includes(p))) return false;
  return true;
}
